import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  BACKEND_URL,
  formatIDR,
  formatDate,
  formatDateTime,
  errMsg,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { AttachmentField, AttachmentLink } from "@/components/TransactionAttachment";
import DateRangeFilter, { useDateFilter } from "@/components/DateRangeFilter";
import { printConsignmentNote, printDeliveryOrder } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, Printer, Truck, Pencil, Camera } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

/**
 * Reusable TransactionForm for Pembelian & Penyaluran.
 * mode: "purchase" | "distribution"
 */
export default function TransactionPage({ mode }) {
  const isPurchase = mode === "purchase";
  const title = isPurchase ? "Pembelian dari Distributor" : "Penyaluran ke Toko Binaan";
  const subtitle = isPurchase
    ? "Setiap pembelian otomatis menambah stok dan kas keluar"
    : "Titip barang ke toko: stok gudang berkurang, stok titipan di toko bertambah. Pembayaran datang saat toko lapor.";
  const listPath = isPurchase ? "purchases" : "distributions";
  const partnerPath = isPurchase ? "distributors" : "stores";
  const partnerLabel = isPurchase ? "Distributor" : "Toko";

  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [storePrices, setStorePrices] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [doDialog, setDoDialog] = useState(null); // for surat jalan
  const [doForm, setDoForm] = useState({ driver: "", vehicle: "", city: "", address: "" });
  const photoInputRef = useRef(null);

  const [form, setForm] = useState({
    partner_id: "",
    date: "",
    notes: "",
    photo_id: null,
    clear_photo: false,
    lines: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const filtered = useDateFilter(items, range);
  const partnerNameKey = isPurchase ? "distributor_name" : "store_name";
  const tf = useTableFilter(filtered, {
    searchKeys: [partnerNameKey, "notes"],
    defaultSort: { key: "date", dir: "desc" },
  });
  const totalSum = tf.rows.reduce((s, x) => s + Number(x.total || 0), 0);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get(`/${listPath}`), api.get(`/${partnerPath}`), api.get("/products"),
    ]);
    setItems(a.data); setPartners(b.data); setProducts(c.data);
  };
  useEffect(() => { load(); }, [listPath]);

  useEffect(() => {
    if (!isPurchase && form.partner_id) {
      api.get(`/store-prices?store_id=${form.partner_id}`).then((r) => setStorePrices(r.data));
    }
  }, [form.partner_id, isPurchase]);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", qty: 1, unit_price: 0, carton_qty: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, key, val) => setForm((f) => {
    const lines = [...f.lines]; lines[i] = { ...lines[i], [key]: val };
    const p = products.find((pp) => pp.id === lines[i].product_id);
    const pack = Number(p?.pack_size || 1);
    // Karton -> pouch conversion
    if (key === "carton_qty") {
      lines[i].qty = Number(val) * pack;
    }
    // Auto price when product selected
    if (key === "product_id") {
      const prod = products.find((pp) => pp.id === val);
      if (prod) {
        if (isPurchase) lines[i].unit_price = prod.purchase_price;
        else {
          const sp = storePrices.find((s) => s.product_id === val);
          lines[i].unit_price = sp ? sp.price : prod.retail_price;
        }
      }
    }
    return { ...f, lines };
  });

  const total = useMemo(() => form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0), [form.lines]);

  const openNew = () => {
    setEditingId(null);
    setAttachment(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setForm({
      partner_id: "",
      date: new Date().toISOString().slice(0, 10),
      notes: "",
      photo_id: null,
      clear_photo: false,
      lines: [{ product_id: "", qty: 1, unit_price: 0, carton_qty: 0 }],
    });
    setOpen(true);
  };

  const photoUrl = (photoId) => {
    const token = localStorage.getItem("hotto_token") || "";
    return `${BACKEND_URL}/api/distribution-photos/${photoId}?auth=${encodeURIComponent(token)}`;
  };

  const handleDistributionPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Gunakan foto JPG, PNG, atau WebP");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Ukuran foto maksimal 10 MB");
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setForm((current) => ({ ...current, clear_photo: false }));
  };

  const clearDistributionPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setForm((current) => ({ ...current, photo_id: null, clear_photo: true }));
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const save = async () => {
    if (saving || uploading) return;
    if (!form.partner_id) return toast.error(`Pilih ${partnerLabel} dulu`);
    if (form.lines.length === 0) return toast.error("Tambahkan minimal 1 item");
    if (form.lines.some((l) => !l.product_id || Number(l.qty) <= 0 || Number(l.unit_price) < 0)) return toast.error("Lengkapi item dengan kuantitas positif dan harga tidak negatif");
    setSaving(true);
    try {
      let photoId = form.photo_id;
      if (!isPurchase && photoFile) {
        const photoData = new FormData();
        photoData.append("file", photoFile);
        const { data } = await api.post("/distribution-photos", photoData);
        photoId = data.id;
      }
      const payload = {
        partner_id: form.partner_id,
        date: form.date || undefined,
        notes: form.notes,
        photo_id: photoId,
        clear_photo: form.clear_photo,
        ...(isPurchase ? { attachment_id: attachment?.id || null } : {}),
        items: form.lines.map((l) => ({
          product_id: l.product_id, qty: Number(l.qty), unit_price: Number(l.unit_price),
        })),
      };
      if (editingId) {
        await api.put(`/${listPath}/${editingId}`, payload);
        toast.success(isPurchase ? "Pembelian diperbarui. Stok dan arus kas disesuaikan." : "Pengiriman diperbarui. Stok gudang dan toko telah disesuaikan.");
      } else {
        await api.post(`/${listPath}`, payload);
        toast.success(
          isPurchase
            ? "Pembelian tersimpan. Stok dan kas telah diperbarui."
            : "Pengiriman tersimpan. Stok gudang dan toko telah diperbarui.",
        );
      }
      setOpen(false);
      setEditingId(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const openEdit = (tx) => {
    setEditingId(tx.id);
    setAttachment(tx.attachment || null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setForm({
      partner_id: isPurchase ? tx.distributor_id : tx.store_id,
      date: (tx.date || "").slice(0, 10),
      notes: tx.notes || "",
      photo_id: tx.photo_id || null,
      clear_photo: false,
      lines: (tx.items || []).map((item) => {
        const product = products.find((candidate) => candidate.id === item.product_id);
        const packSize = Number(product?.pack_size || item.pack_size || 1);
        return {
          product_id: item.product_id,
          qty: item.qty,
          unit_price: item.unit_price,
          carton_qty: packSize > 1 ? Number(item.qty) / packSize : 0,
        };
      }),
    });
    setDetail(null);
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Hapus transaksi ini? Stok & kas akan dikembalikan.")) return;
    try { await api.delete(`/${listPath}/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader
        title={title} subtitle={subtitle}
        testId={`page-${listPath}`}
        actions={
          <Button onClick={openNew} data-testid={`btn-add-${listPath}`} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            <Plus className="w-4 h-4 mr-2"/> Transaksi Baru
          </Button>
        }
      />

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[var(--hotto-border)] space-y-3">
          <DateRangeFilter value={range} onChange={setRange}/>
          <div className="flex justify-end">
            <span className="text-sm text-slate-500 mr-3">Total di rentang tanggal:</span>
            <span className="font-mono-num font-bold text-[var(--hotto-primary)]">{formatIDR(totalSum)}</span>
            <span className="text-xs text-slate-500 ml-2 mt-1">({tf.rows.length} transaksi)</span>
          </div>
        </div>
        <div className="px-4 pb-3">
          <TableToolbar tf={tf} placeholder={`Cari ${partnerLabel.toLowerCase()} / catatan...`} testId={`search-${listPath}`}/>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={tf} col="date" className="w-40">{isPurchase ? "Tanggal Beli" : "Tanggal Titip"}</SortHeader>
              <SortHeader tf={tf} col={partnerNameKey} className="w-52">{partnerLabel}</SortHeader>
              <TableHead>{isPurchase ? "Detail Pembelian" : "Detail Item Dititipkan"}</TableHead>
              {isPurchase && <TableHead>Bukti Transfer</TableHead>}
              <SortHeader tf={tf} col="total" className="text-right w-36">Total</SortHeader>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tf.rows.length === 0 && (
              <TableRow><TableCell colSpan={isPurchase ? 7 : 6} className="text-center py-8 text-slate-500">Belum ada transaksi.</TableCell></TableRow>
            )}
            {tf.rows.map((tx, idx) => (
              <TableRow key={tx.id} className="table-row-hover align-top">
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-mono-num text-sm">
                  <div className="font-semibold">{formatDate(tx.date)}</div>
                  <div className="text-xs text-slate-500">{formatDateTime(tx.date).split(" ").slice(-2).join(" ")}</div>
                </TableCell>
                <TableCell className="font-medium">
                  {isPurchase ? tx.distributor_name : tx.store_name}
                  {tx.notes && <div className="text-xs text-slate-500 mt-1 italic">"{tx.notes}"</div>}
                  {!isPurchase && tx.photo_id && (
                    <a
                      href={photoUrl(tx.photo_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-sky-700 hover:underline"
                      data-testid={`link-distribution-photo-${tx.id}`}
                    >
                      Lihat bukti foto
                    </a>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="space-y-1">
                    {tx.items.slice(0, 4).map((it, i) => (
                      <div key={i} className="flex items-baseline gap-2">
                        <span className="text-slate-400 font-mono-num text-xs">•</span>
                        <span className="flex-1 text-slate-700">{it.product_name}</span>
                        <span className="font-mono-num font-semibold text-slate-900">{it.qty}</span>
                        <span className="text-xs text-slate-500 min-w-[35px]">{it.unit || "pcs"}</span>
                      </div>
                    ))}
                    {tx.items.length > 4 && (
                      <div className="text-xs text-slate-400 italic pl-4">+ {tx.items.length - 4} produk lain...</div>
                    )}
                  </div>
                </TableCell>
                {isPurchase && <TableCell><AttachmentLink attachment={tx.attachment} testId={`purchase-proof-${tx.id}`}/></TableCell>}
                <TableCell className="text-right font-mono-num font-semibold">
                  {formatIDR(tx.total)}
                  <div className="text-xs font-normal text-slate-500">{tx.items.length} SKU</div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(tx)} data-testid={`btn-edit-${mode}-${tx.id}`} title={isPurchase ? "Edit Pembelian" : "Edit Pengiriman"}><Pencil className="w-4 h-4 text-slate-700"/></Button>
                  {!isPurchase && (
                    <>
                      <Button size="icon" variant="ghost" onClick={()=>printConsignmentNote(tx)} data-testid={`btn-print-${tx.id}`} title="Cetak Nota Titipan">
                        <Printer className="w-4 h-4 text-[var(--hotto-primary)]"/>
                      </Button>
                      <Button size="icon" variant="ghost" onClick={()=>{
                        const st = partners.find((p)=>p.id===tx.store_id);
                        setDoForm({ driver: "", vehicle: "", city: "", address: st?.address || "" });
                        setDoDialog(tx);
                      }} data-testid={`btn-do-${tx.id}`} title="Cetak Surat Jalan">
                        <Truck className="w-4 h-4 text-slate-700"/>
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={()=>setDetail(tx)} title="Lihat Detail"><ChevronRight className="w-4 h-4"/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(tx.id)} title="Hapus"><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={(value) => { if (!saving && !uploading) setOpen(value); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`dialog-${listPath}`}>
          <DialogHeader>
            <DialogTitle>{editingId ? (isPurchase ? "Edit Pembelian dari Distributor" : "Edit Pengiriman ke Toko Binaan") : title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>
                  {partnerLabel}
                  {editingId && !isPurchase && <span className="text-xs text-slate-400"> (tidak dapat diubah)</span>}
                </Label>
                <Select
                  value={form.partner_id}
                  onValueChange={(v)=>setForm({...form,partner_id:v})}
                  disabled={Boolean(editingId) && !isPurchase}
                >
                  <SelectTrigger data-testid="select-partner"><SelectValue placeholder={`Pilih ${partnerLabel}`}/></SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{isPurchase ? "Tanggal Pembelian" : "Tanggal Penyaluran"}</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid={`input-${mode}-date`}
                />
              </div>
              <div>
                <Label>Catatan (opsional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="input-transaction-notes"
                />
              </div>
              {isPurchase && <AttachmentField kind="purchase-proof" value={attachment} onChange={setAttachment} onBusyChange={setUploading} disabled={saving} testId="input-purchase-proof"/>}
              {!isPurchase && (
                <div>
                  <Label>Bukti Foto Pengiriman</Label>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleDistributionPhoto}
                    className="hidden"
                    data-testid="input-distribution-photo"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => photoInputRef.current?.click()}
                      data-testid="btn-pick-distribution-photo"
                    >
                      <Camera className="w-3 h-3 mr-1" /> Pilih Foto
                    </Button>
                    {(photoPreview || form.photo_id) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={clearDistributionPhoto}
                        data-testid="btn-clear-distribution-photo"
                      >
                        Hapus
                      </Button>
                    )}
                  </div>
                  {(photoPreview || form.photo_id) && (
                    <img
                      src={photoPreview || photoUrl(form.photo_id)}
                      alt="Pratinjau bukti pengiriman"
                      className="mt-2 h-16 w-24 rounded border object-cover"
                      data-testid="distribution-photo-preview"
                    />
                  )}
                  <div className="mt-1 text-xs text-slate-500">Otomatis dikompres ke WebP.</div>
                </div>
              )}
            </div>

            <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="min-w-[220px]">Produk</TableHead>
                    <TableHead className="w-24 text-center">Karton</TableHead>
                    <TableHead className="w-28 text-center">Total (pcs)</TableHead>
                    <TableHead className="w-40">Harga / pcs</TableHead>
                    <TableHead className="w-40">Harga / Karton</TableHead>
                    <TableHead className="w-36 text-right">Subtotal</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.lines.map((l, i) => {
                    const p = products.find((x)=>x.id===l.product_id);
                    const pack = Number(p?.pack_size || 1);
                    const cartonPrice = Math.round(Number(l.unit_price || 0) * pack);
                    return (
                    <TableRow key={i}>
                      <TableCell>
                        <Select value={l.product_id} onValueChange={(v)=>updateLine(i,"product_id",v)}>
                          <SelectTrigger data-testid={`select-product-${i}`}><SelectValue placeholder="Pilih produk"/></SelectTrigger>
                          <SelectContent>
                            {products.map((pp)=><SelectItem key={pp.id} value={pp.id}>{pp.name} · stok {pp.stock} {pp.unit} {pp.pack_size>1?`(${pp.pack_size}/kart)`:""}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {p && !isPurchase && <div className="text-xs text-slate-500 mt-1">Stok saat ini: {p.stock} {p.unit}</div>}
                        {p && pack>1 && <div className="text-xs text-emerald-700 mt-1">1 karton = {pack} {p.unit}</div>}
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={l.carton_qty || 0} onChange={(e)=>updateLine(i,"carton_qty",e.target.value)} data-testid={`input-carton-${i}`} disabled={!p || pack<=1} placeholder="0"/>
                      </TableCell>
                      <TableCell><Input type="number" value={l.qty} onChange={(e)=>updateLine(i,"qty",e.target.value)} data-testid={`input-qty-${i}`}/></TableCell>
                      <TableCell><Input type="number" value={l.unit_price} onChange={(e)=>updateLine(i,"unit_price",e.target.value)} data-testid={`input-price-${i}`}/></TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={cartonPrice}
                          disabled={pack<=1}
                          onChange={(e)=>{
                            const perUnit = pack > 0 ? Number(e.target.value) / pack : Number(e.target.value);
                            updateLine(i, "unit_price", perUnit);
                          }}
                          data-testid={`input-carton-price-${i}`}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(Number(l.qty||0)*Number(l.unit_price||0))}</TableCell>
                      <TableCell><Button size="icon" variant="ghost" onClick={()=>removeLine(i)}><Trash2 className="w-4 h-4 text-red-500"/></Button></TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
              <div className="p-3 border-t border-[var(--hotto-border)] flex justify-between items-center bg-slate-50/50">
                <Button variant="outline" size="sm" onClick={addLine} data-testid="btn-add-line"><Plus className="w-4 h-4 mr-1"/> Tambah Item</Button>
                <div className="flex items-center gap-4">
                  <span className="overline">Total</span>
                  <span className="font-mono-num font-bold text-xl">{formatIDR(total)}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setOpen(false); setEditingId(null); }} variant="outline" disabled={saving || uploading} data-testid="btn-cancel-transaction">
              Batal
            </Button>
            <Button
              onClick={save}
              data-testid="btn-save-tx"
              disabled={saving || uploading}
              className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]"
            >
              {saving ? "Menyimpan..." : uploading ? "Menunggu unggahan..." : editingId ? "Simpan Perubahan" : "Simpan Transaksi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detail Transaksi</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">{partnerLabel}:</span> <b>{isPurchase ? detail.distributor_name : detail.store_name}</b></div>
                <div><span className="text-slate-500">Tanggal:</span> {formatDate(detail.date)}</div>
                {detail.notes && <div className="col-span-2"><span className="text-slate-500">Catatan:</span> {detail.notes}</div>}
                {isPurchase && <div className="col-span-2">Bukti transfer: <AttachmentLink attachment={detail.attachment} testId="purchase-detail-proof"/></div>}
                {!isPurchase && detail.photo_id && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Bukti foto:</span>
                    <img
                      src={photoUrl(detail.photo_id)}
                      alt="Bukti pengiriman"
                      className="mt-2 max-h-52 rounded border object-contain"
                      data-testid="distribution-photo-detail"
                    />
                  </div>
                )}
              </div>
              <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Produk</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {detail.items.map((it,i)=>(
                      <TableRow key={i}>
                        <TableCell>{it.product_name}</TableCell>
                        <TableCell className="text-right font-mono-num">{it.qty}</TableCell>
                        <TableCell className="text-right font-mono-num">{formatIDR(it.unit_price)}</TableCell>
                        <TableCell className="text-right font-mono-num">{formatIDR(it.subtotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between items-center">
                {!isPurchase && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(detail)}
                      data-testid={`btn-edit-detail-distribution-${detail.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" /> Edit Pengiriman
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>printConsignmentNote(detail)} data-testid="btn-print-detail">
                      <Printer className="w-4 h-4 mr-2"/> Cetak Nota Titipan (PDF)
                    </Button>
                  </div>
                )}
                <div className={!isPurchase ? "" : "ml-auto"}>
                  <span className="overline mr-3">Total</span><span className="font-mono-num font-bold text-lg">{formatIDR(detail.total)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Surat Jalan Dialog */}
      <Dialog open={!!doDialog} onOpenChange={(v)=>!v && setDoDialog(null)}>
        <DialogContent data-testid="dialog-surat-jalan">
          <DialogHeader><DialogTitle>Buat Surat Jalan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kurir / Driver</Label><Input value={doForm.driver} onChange={(e)=>setDoForm({...doForm,driver:e.target.value})} data-testid="input-driver"/></div>
              <div><Label>Nomor Kendaraan</Label><Input value={doForm.vehicle} onChange={(e)=>setDoForm({...doForm,vehicle:e.target.value})} placeholder="B 1234 XYZ" data-testid="input-vehicle"/></div>
            </div>
            <div><Label>Tujuan Kota</Label><Input value={doForm.city} onChange={(e)=>setDoForm({...doForm,city:e.target.value})} placeholder="Jakarta" data-testid="input-city"/></div>
            <div><Label>Alamat Lengkap</Label><Textarea rows={3} value={doForm.address} onChange={(e)=>setDoForm({...doForm,address:e.target.value})} data-testid="input-address"/></div>
            {doDialog && (
              <div className="text-sm text-slate-600 border border-[var(--hotto-border)] rounded-md p-3 bg-slate-50">
                <div><b>Penerima:</b> {doDialog.store_name}</div>
                <div><b>Tanggal Kirim:</b> {formatDate(doDialog.date)}</div>
                <div><b>Jumlah Item:</b> {doDialog.items.length} produk</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDoDialog(null)}>Batal</Button>
            <Button
              onClick={()=>{ printDeliveryOrder(doDialog, {...doForm, products}); setDoDialog(null); }}
              data-testid="btn-generate-do"
              className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]"
            ><Truck className="w-4 h-4 mr-2"/> Cetak Surat Jalan (PDF)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
